import React, { useState, useEffect } from 'react';
import { Button, Input, Select } from '@/components/ui';
import type { ResourceLevel, ResourceLevelCategory } from '@/types';

interface ResourceLevelFormProps {
  initialData?: ResourceLevel | null;
  onSubmit: (data: Partial<ResourceLevel>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CATEGORY_OPTIONS = [
  { value: '', label: 'Select Category...' },
  { value: 'TECHNICAL', label: 'Technical' },
  { value: 'MANAGEMENT', label: 'Management' },
  { value: 'SPECIALIZED', label: 'Specialized' },
];

const ResourceLevelForm: React.FC<ResourceLevelFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    code: '',
    displayName: '',
    description: '',
    category: '' as ResourceLevelCategory | '',
    sortOrder: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        code: initialData.code,
        displayName: initialData.displayName,
        description: initialData.description || '',
        category: initialData.category,
        sortOrder: initialData.sortOrder.toString(),
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.code) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.code)) {
      newErrors.code = 'Code must contain only uppercase letters, numbers, and underscores';
    } else if (formData.code.length > 20) {
      newErrors.code = 'Code must be 20 characters or less';
    }

    if (!formData.displayName) {
      newErrors.displayName = 'Display name is required';
    } else if (formData.displayName.length > 100) {
      newErrors.displayName = 'Display name must be 100 characters or less';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.sortOrder) {
      newErrors.sortOrder = 'Sort order is required';
    } else {
      const sortOrder = parseInt(formData.sortOrder);
      if (isNaN(sortOrder) || sortOrder <= 0) {
        newErrors.sortOrder = 'Sort order must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: any = {
      code: formData.code.toUpperCase(), // Ensure uppercase
      displayName: formData.displayName,
      category: formData.category as ResourceLevelCategory,
      sortOrder: parseInt(formData.sortOrder),
    };

    // Only include description if it's not empty
    if (formData.description.trim()) {
      submitData.description = formData.description.trim();
    }

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const isEditMode = !!initialData;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <Input
          label="Code"
          type="text"
          value={formData.code}
          onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
          error={errors.code}
          placeholder="e.g., T7, M6"
          required
          fullWidth
          disabled={isEditMode}
          helperText={
            isEditMode
              ? 'Code cannot be changed after creation'
              : 'Unique identifier (uppercase letters, numbers, underscores only)'
          }
          maxLength={20}
        />

        <Input
          label="Display Name"
          type="text"
          value={formData.displayName}
          onChange={(e) => handleInputChange('displayName', e.target.value)}
          error={errors.displayName}
          placeholder="e.g., Technical Level 7"
          required
          fullWidth
          maxLength={100}
          helperText="User-friendly name shown in dropdowns and tables"
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Description <span className="text-muted-foreground">(Optional)</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue"
            placeholder="Brief description of this resource level..."
          />
          <p className="mt-1 text-sm text-muted-foreground">
            Optional details about experience level, responsibilities, etc.
          </p>
        </div>

        <Select
          label="Category"
          value={formData.category}
          onChange={(e) => handleInputChange('category', e.target.value)}
          options={CATEGORY_OPTIONS}
          error={errors.category}
          required
          fullWidth
          helperText="Group this level belongs to (Technical, Management, or Specialized)"
        />

        <Input
          label="Sort Order"
          type="number"
          step="1"
          min="1"
          value={formData.sortOrder}
          onChange={(e) => handleInputChange('sortOrder', e.target.value)}
          error={errors.sortOrder}
          placeholder="e.g., 70"
          required
          fullWidth
          helperText="Controls display order in dropdowns (lower numbers appear first)"
        />
      </div>

      <div className="p-4 bg-muted/50 rounded-lg border border-border">
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">Note:</strong> After creating this resource level, you'll need to
          configure rate cards for each fiscal year on the Rate Cards page.
        </p>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading} disabled={isLoading}>
          {initialData ? 'Update' : 'Create'} Resource Level
        </Button>
      </div>
    </form>
  );
};

export default ResourceLevelForm;
